import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Share2, Minus, Plus, Package } from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import api, { formatCurrency } from '../lib/api';
import { toast } from 'sonner';

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart, updateQuantity } = useCart();
  const { isAuthenticated } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [observations, setObservations] = useState('');
  const [quantity, setQuantity] = useState(1);

  const cartItem = cart.items.find(item => item.product_id === id);
  const cartQuantity = cartItem?.quantity || 0;

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        toast.error('Produto não encontrado');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [id, navigate]);

  const discount = product?.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  const handleAddToCart = async () => {
    await addToCart(id, quantity, product);
  };

  const handleShare = () => {
    const url = window.location.href;
    const text = `Confira ${product.name} por apenas ${formatCurrency(product.price)}!`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <Skeleton className="aspect-square rounded-2xl" />
            <div className="space-y-4">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-12 w-1/3" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-white shadow-sm">
              <img
                src={product.images?.[selectedImage] || 'https://images.unsplash.com/photo-1612708074992-c7936e82c99b?w=800'}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
              
              {discount > 0 && (
                <div className="absolute top-4 left-4 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm font-bold">
                  -{discount}%
                </div>
              )}
              
              {product.is_kit && (
                <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                  <Package className="w-4 h-4" />
                  KIT
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-colors ${
                      selectedImage === index ? 'border-primary' : 'border-transparent'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Video */}
            {product.video_url && (
              <div className="aspect-video rounded-2xl overflow-hidden bg-black">
                <video
                  src={product.video_url}
                  controls
                  className="w-full h-full"
                  data-testid="product-video"
                />
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground mb-2" data-testid="product-name">
                {product.name}
              </h1>
              
              {product.stock <= product.min_stock && product.stock > 0 && (
                <p className="text-sm text-warning font-medium">
                  ⚠️ Últimas unidades disponíveis
                </p>
              )}
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="text-4xl font-extrabold text-primary font-heading" data-testid="product-price">
                {formatCurrency(product.price)}
              </span>
              {product.original_price && (
                <span className="text-xl line-through text-muted-foreground">
                  {formatCurrency(product.original_price)}
                </span>
              )}
            </div>

            {/* Description */}
            <div className="prose prose-slate max-w-none">
              <p className="text-muted-foreground leading-relaxed" data-testid="product-description">
                {product.description}
              </p>
            </div>

            {/* Stock Info */}
            <div className="bg-slate-100 rounded-xl p-4">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{product.stock}</span> unidades em estoque
              </p>
            </div>

            {/* Observations */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Observações (opcional)
              </label>
              <Textarea
                placeholder="Ex: Preferência por marcas específicas, alergias, etc."
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                className="rounded-xl"
                data-testid="observations-input"
              />
            </div>

            {/* Quantity & Add to Cart */}
            {product.stock > 0 ? (
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Quantity Selector */}
                <div className="flex items-center border rounded-xl p-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    data-testid="decrease-quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-12 text-center font-bold" data-testid="quantity-display">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    disabled={quantity >= product.stock}
                    data-testid="increase-quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Add to Cart Button */}
                <Button
                  size="lg"
                  className="flex-1 rounded-xl shadow-primary"
                  onClick={handleAddToCart}
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Adicionar ao Carrinho
                </Button>
              </div>
            ) : (
              <Button size="lg" disabled className="w-full rounded-xl">
                Produto Esgotado
              </Button>
            )}

            {cartQuantity > 0 && (
              <p className="text-sm text-muted-foreground text-center">
                Você já tem <span className="font-bold text-primary">{cartQuantity}</span> {cartQuantity === 1 ? 'unidade' : 'unidades'} no carrinho
              </p>
            )}

            {/* Share Button */}
            <Button
              variant="outline"
              className="w-full rounded-xl"
              onClick={handleShare}
              data-testid="share-button"
            >
              <Share2 className="mr-2 h-4 w-4" />
              Compartilhar no WhatsApp
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProductPage;
